import React, { useState, useEffect } from "react";
import { Card, Typography } from "@material-tailwind/react";
import Popup from "reactjs-popup";
import "../../../../styles/popup.css";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import API from "../../../../utils/API";
import CustomTable from "../../../DynamicComponents/CustomTable";
import { useThemeContext } from "../../../../context/ThemeContext";
import AchievementPopUp from "../PopUp/AchievementsPopUp";
import {
  formatDateForDisplay,
  formatDateForPopUp,
} from "../../../../utils/Common";

const AlumniAchievements = ({ setBlurActive }) => {
  const [achievements, setAchievements] = useState([]);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [selectedAchievement, setSelectedAchievement] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const user = useSelector((state) => state.auth.user) || {};
  const { user_id, department_id } = user;
  const { darkMode } = useThemeContext();

  useEffect(() => {
    if (!user_id) return;

    const fetchAchievements = async () => {
      try {
        const response = await API.get(
          `${department_id}/alumni/achievement?user_id=${user_id}`
        );
        const formattedAchievements = (response?.data || []).map(
          (achievement) => ({
            ...achievement,
            date_received: formatDateForDisplay(achievement.date_received),
          })
        );

        setAchievements(formattedAchievements || []);
      } catch (error) {
        toast.error(
          error?.response?.data?.error || "Error fetching achievements"
        );
      }
    };

    fetchAchievements();
  }, [user_id]);

  const openAddPopup = () => {
    setSelectedAchievement(null);
    setIsEditMode(false);
    setPopupOpen(true);
    setBlurActive(true);
  };

  const openEditPopup = (achievement) => {
    setSelectedAchievement(achievement);
    setIsEditMode(true);
    setPopupOpen(true);
    setBlurActive(true);
  };

  const closePopup = () => {
    setPopupOpen(false);
    setBlurActive(false);
  };

  const handleAddAchievement = async (achievementData) => {
    try {
      const response = await API.post(
        `${department_id}/alumni/achievement?user_id=${user_id}`,
        achievementData
      );

      if (response && response.data) {
        toast.success("Achievement added successfully");
        // Refresh the achievements list
        const updatedResponse = await API.get(
          `${department_id}/alumni/achievement?user_id=${user_id}`
        );
        setAchievements(updatedResponse.data || []);
        closePopup();
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || "Error adding achievement");
    }
  };

  const handleUpdateAchievement = async (achievementData) => {
    try {
      const response = await API.put(
        `${department_id}/alumni/achievement/${selectedAchievement.achievement_id}`,
        achievementData
      );

      if (response && response.data) {
        toast.success("Achievement updated successfully");
        // Refresh the achievements list
        const updatedResponse = await API.get(
          `${department_id}/alumni/achievement?user_id=${user_id}`
        );
        setAchievements(updatedResponse.data || []);
        closePopup();
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || "Error updating achievement");
    }
  };

  const handleDeleteAchievement = async (achievementId) => {
    try {
      await API.delete(`${department_id}/alumni/achievement/${achievementId}`);
      toast.success("Achievement deleted successfully");
      // Refresh the achievements list
      const updatedResponse = await API.get(
        `${department_id}/alumni/achievement?user_id=${user_id}`
      );
      setAchievements(updatedResponse.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Error deleting achievement");
    }
  };

  return (
    <>
      <CustomTable
        title="Achievements"
        subtitle="Your professional and academic accomplishments"
        columns={[
          { key: "title", label: "Title" },
          { key: "issuing_organization", label: "Issuing Organization" },
          {
            key: "date_received",
            label: "Date Received",
          },
          { key: "nature_name", label: "Nature" },
          { key: "country_location", label: "Country" },
          { key: "actions", label: "Actions" },
        ]}
        data={achievements}
        actions={{
          edit: (achievement) => openEditPopup(achievement),
          delete: (achievement) =>
            handleDeleteAchievement(achievement.achievement_id),
        }}
        onAdd={openAddPopup}
      />

      <Popup
        open={isPopupOpen}
        onClose={closePopup}
        className="mx-auto my-auto p-2"
        closeOnDocumentClick
      >
        <div>
          <AchievementPopUp
            achievement={selectedAchievement}
            isEditMode={isEditMode}
            closeModal={closePopup}
            handleAddAchievement={handleAddAchievement}
            handleUpdateAchievement={handleUpdateAchievement}
          />
        </div>
      </Popup>
    </>
  );
};

export default AlumniAchievements;
